'use client';

import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../components/KeycloakProvider';
import { Plus, Kanban, Users, ArrowRight, Sparkles, ShieldCheck, LogIn, UserPlus, Clock, CheckCircle2, Archive, PauseCircle } from 'lucide-react';
import { api } from '../lib/api';
import Link from 'next/link';

import { useToast } from '../components/ui/Toast';
import PageLoader from '../components/PageLoader';
import {CustomerWorkspaceAccess} from '../components/CustomerWorkspaceAccess';
import KycSubmissionModal from '../components/KycSubmissionModal';
import PendingMemberRequests from '../components/PendingMemberRequests';
import { AlertTriangle, Lock } from 'lucide-react';

export default function HomePage() {
  const { authenticated, login, register } = useAuth();
  const { showToast } = useToast();
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  const [newWsName, setNewWsName] = useState('');
  const [newWsSlug, setNewWsSlug] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showKycModal, setShowKycModal] = useState(false);

  const fetchUserData = async () => {
    if (!authenticated) return;
    setLoading(true);
    try {
      const res = await api.get('/auth/me');
      if (res.data?.data) {
        setCurrentUser(res.data.data);
        if (res.data.data.workspaceMembers) {
          const userId = res.data.data.id;
          const wsList = res.data.data.workspaceMembers.map((m: any) => ({
            workspace: m.workspace,
            role: m.role,
            userId: userId,
          }));
          setWorkspaces(wsList);
        }
      }
    } catch (err) {
      console.error('Failed to fetch user context', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [authenticated]);

  const [errorMessage, setErrorMessage] = useState('');

  const isOrg = currentUser?.userType === 'ORGANIZATION';
  const kycStatus = currentUser?.kycStatus || currentUser?.organizationKyc?.status;

  const isKycSubmitted = isOrg && Boolean(currentUser?.organizationKyc) && (kycStatus === 'PENDING' || currentUser?.organizationKyc?.status === 'PENDING');
  const isKycRejected = isOrg && Boolean(currentUser?.organizationKyc) && (kycStatus === 'REJECTED' || currentUser?.organizationKyc?.status === 'REJECTED');
  const isKycRestricted = isOrg && currentUser?.accountStatus !== 'ACTIVE' && kycStatus !== 'APPROVED';

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isKycRestricted) {
      showToast('Account is PENDING KYC verification. Workspace creation is locked.', 'error');
      return;
    }
    setErrorMessage('');
    const cleanSlug = newWsSlug.toLowerCase().trim().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    if (!newWsName || !cleanSlug) return;
    try {
      const created = await api.post('/workspaces', { name: newWsName, slug: cleanSlug });
      showToast(`🎉 Workspace "${newWsName}" created successfully!`, 'success');
      setNewWsName('');
      setNewWsSlug('');
      setShowModal(false);
      fetchUserData();
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        (Array.isArray(err?.response?.data?.details) ? err.response.data.details.map((d: any) => d.message).join('. ') : null) ||
        'Failed to create workspace. Ensure workspace slug is unique.';
      showToast(backendMessage, 'error');
      setErrorMessage(backendMessage);
    }
  };

  const workspaceSections = [
    { key: 'IN_PROGRESS', label: 'In Progress', icon: Clock, color: 'text-indigo-300', dot: 'bg-indigo-400' },
    { key: 'ON_HOLD', label: 'On Hold', icon: PauseCircle, color: 'text-amber-300', dot: 'bg-amber-400' },
    { key: 'COMPLETED', label: 'Completed', icon: CheckCircle2, color: 'text-emerald-300', dot: 'bg-emerald-400' },
    { key: 'ARCHIVED', label: 'Archive', icon: Archive, color: 'text-slate-300', dot: 'bg-slate-500' },
  ];

  const handleUpdateWorkspaceStatus = async (workspaceId: string, status: string) => {
    try {
      await api.patch(`/workspaces/${workspaceId}`, { status });
      showToast('Workspace section updated', 'success');
      fetchUserData();
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to update workspace section', 'error');
    }
  };

  const renderWorkspaceCard = (wm: any, accent: any) => {
    const ws = wm.workspace;
    const role = wm.role;
    const canManageStatus = role === 'OWNER' || role === 'ADMIN' || ws.createdBy === wm.userId || currentUser?.isSuperadmin;

    return (
      <div
        key={ws.id}
        className="bg-slate-900/60 hover:bg-slate-900 border border-slate-800 hover:border-slate-700 rounded-2xl p-6 transition-all group shadow-sm flex flex-col justify-between"
      >
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300">
              {role === 'OWNER' || ws.createdBy === wm.userId ? <Kanban className="w-5 h-5" /> : <Users className="w-5 h-5" />}
            </div>
            <span
              className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full font-mono uppercase tracking-wider ${
                role === 'OWNER'
                  ? 'text-amber-300 bg-amber-500/10 border border-amber-500/20'
                  : role === 'ADMIN'
                  ? 'text-indigo-300 bg-indigo-500/10 border border-indigo-500/20'
                  : role === 'GUEST'
                  ? 'text-slate-400 bg-slate-800 border border-slate-700'
                  : 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/20'
              }`}
            >
              {role}
            </span>
          </div>
          <h3 className={`text-lg font-bold text-slate-100 group-hover:${accent.color} transition-colors`}>
            {ws.name}
          </h3>
          <p className="text-xs text-slate-400 font-mono mt-1">/{ws.slug}</p>
        </div>

        <div className="mt-6 pt-4 border-t border-slate-800/80 flex items-center justify-between gap-3">
          {canManageStatus ? (
            <select
              value={ws.status || 'IN_PROGRESS'}
              onChange={(e) => handleUpdateWorkspaceStatus(ws.id, e.target.value)}
              className="bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-[11px] text-slate-200 focus:outline-none focus:border-indigo-500"
            >
              {workspaceSections.map((section) => (
                <option key={section.key} value={section.key}>{section.label}</option>
              ))}
            </select>
          ) : (
            <span className={`text-[11px] font-medium ${accent.color}`}>{accent.label}</span>
          )}
          <a
            href={`/workspace/${ws.id}`}
            className={`text-xs font-semibold ${accent.color} hover:text-white flex items-center gap-1 shrink-0`}
          >
            Open <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    );
  };

  const renderWorkspaceCollection = (title: string, items: any[], icon: React.ReactNode) => {
    if (items.length === 0) return null;

    return (
      <div>
        <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-300 mb-5 flex items-center gap-2">
          {icon}
          {title} ({items.length})
        </h3>
        <div className="space-y-8">
          {workspaceSections.map((section) => {
            const sectionWorkspaces = items.filter((wm) => (wm.workspace?.status || 'IN_PROGRESS') === section.key);
            if (sectionWorkspaces.length === 0) return null;
            const SectionIcon = section.icon;

            return (
              <div key={section.key}>
                <h4 className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${section.dot}`}></span>
                  <SectionIcon className={`w-3.5 h-3.5 ${section.color}`} />
                  {section.label} ({sectionWorkspaces.length})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sectionWorkspaces.map((wm) => renderWorkspaceCard(wm, section))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // If not authenticated, show ONLY Log In and Create Account options
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col font-sans relative overflow-hidden">
        <Navbar />

        {/* Glow Effects */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-3xl pointer-events-none"></div>

        <main className="flex-1 flex items-center justify-center max-w-4xl w-full mx-auto px-6 py-16 text-center relative z-10">
          <div className="bg-slate-900/70 border border-slate-800 backdrop-blur-2xl rounded-3xl p-8 sm:p-12 shadow-2xl max-w-lg w-full">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-indigo-600 to-violet-600 flex items-center justify-center text-white mx-auto mb-6 shadow-xl shadow-indigo-500/20">
              <Kanban className="w-8 h-8" />
            </div>
            
            <h1 className="text-3xl font-extrabold text-white tracking-tight">ScaleSutra Management</h1>
            <p className="text-sm text-slate-400 mt-2.5 mb-8 leading-relaxed font-medium">
              Enterprise Task & Kanban Workspace platform. Please log in or create an account to access your workspace.
            </p>

            <div className="flex flex-col gap-3.5">
              <Link
                href="/login"
                className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-100 font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md"
              >
                <LogIn className="w-4 h-4 text-indigo-400" />
                <span>Log In</span>
              </Link>

              <Link
                href="/signup"
                className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm rounded-xl shadow-xl shadow-indigo-600/25 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <UserPlus className="w-4 h-4" />
                <span>Create Account</span>
              </Link>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-800/80 flex items-center justify-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>ScaleSutra Enterprise Security</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if(currentUser?.role==='WHATSAPP_CUSTOMER')return <CustomerWorkspaceAccess/>;
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col font-sans">
      <Navbar />

      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-10">
        {/* Pending / Submitted KYC Alert Banner for Organizations */}
        {isOrg && isKycRestricted && (
          <div className={`mb-8 p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border ${
            isKycSubmitted 
              ? 'bg-indigo-500/10 border-indigo-500/30' 
              : isKycRejected 
              ? 'bg-rose-500/10 border-rose-500/30'
              : 'bg-amber-500/10 border-amber-500/30'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${
                isKycSubmitted
                  ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400'
                  : isKycRejected
                  ? 'bg-rose-500/20 border-rose-500/30 text-rose-400'
                  : 'bg-amber-500/20 border-amber-500/30 text-amber-400'
              }`}>
                {isKycSubmitted ? <ShieldCheck className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
              </div>
              <div>
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <span>
                    {isKycSubmitted 
                      ? 'KYC Verification Submitted (Under Review)' 
                      : isKycRejected 
                      ? 'KYC Application Rejected' 
                      : 'Identity Verification (KYC) Required'}
                  </span>
                  <span className={`px-2 py-0.5 text-[10px] font-extrabold rounded-md uppercase border ${
                    isKycSubmitted
                      ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-300'
                      : isKycRejected
                      ? 'bg-rose-500/20 border-rose-500/30 text-rose-300'
                      : 'bg-amber-500/20 border-amber-500/30 text-amber-300'
                  }`}>
                    {isKycSubmitted ? 'Pending Superadmin Approval' : isKycRejected ? 'Action Required' : 'Restricted Mode'}
                  </span>
                </h3>
                <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                  {isKycSubmitted ? (
                    <span>Your organization details and identity document have been received. Superadmin is reviewing your application. Creation privileges will unlock immediately upon approval.</span>
                  ) : isKycRejected ? (
                    <span>Rejection Reason: <strong className="text-rose-300">{currentUser?.organizationKyc?.rejectionReason || 'Document details need revision.'}</strong>. Please update and re-submit your document.</span>
                  ) : (
                    <span>Your Organization account is created in restricted mode. Submit your company details & national ID proof to unlock workspace & board creation after Superadmin approval.</span>
                  )}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowKycModal(true)}
              className={`px-4 py-2.5 font-bold text-xs rounded-xl shadow-lg shrink-0 flex items-center gap-2 transition-all cursor-pointer ${
                isKycSubmitted
                  ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/20'
                  : isKycRejected
                  ? 'bg-rose-600 hover:bg-rose-500 text-white shadow-rose-600/20'
                  : 'bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-amber-500/20'
              }`}
            >
              <ShieldCheck className="w-4 h-4" />
              <span>
                {isKycSubmitted ? 'View / Update KYC Details' : isKycRejected ? 'Re-submit KYC Verification' : 'Submit KYC Verification'}
              </span>
            </button>
          </div>
        )}

        {/* Pending Member Requests Component for Org Admins */}
        <PendingMemberRequests />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
              Workspaces <Sparkles className="w-5 h-5 text-indigo-400" />
            </h2>
            <p className="text-sm text-slate-400">Select a workspace or create a new team board space</p>
            {authenticated&&<Link className="mt-2 inline-block text-sm font-semibold text-indigo-300 underline" href="/onboarding">Guided customer / project onboarding →</Link>}
          </div>
          {authenticated && (
            <button
              disabled={isKycRestricted}
              onClick={() => {
                if (isKycRestricted) {
                  showToast(
                    isKycSubmitted
                      ? 'KYC verification is currently under review by Superadmin. Workspace creation will unlock upon approval.'
                      : 'Please submit your KYC identity document to enable workspace creation.',
                    'error'
                  );
                  return;
                }
                setShowModal(true);
              }}
              className={`px-4 py-2.5 text-white font-medium text-xs sm:text-sm rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all shrink-0 ${
                isKycRestricted
                  ? 'bg-slate-800/80 border border-slate-800 text-slate-500 opacity-60 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-600/20 cursor-pointer'
              }`}
            >
              {isKycRestricted ? <Lock className="w-4 h-4 text-amber-400" /> : <Plus className="w-4 h-4" />}
              <span>{isKycRestricted ? (isKycSubmitted ? 'Locked (Under Review)' : 'Locked (KYC Required)') : 'Create Workspace'}</span>
            </button>
          )}
        </div>

        {loading ? (
          <PageLoader type="grid" />
        ) : workspaces.length > 0 ? (
          <div className="space-y-10">
            {renderWorkspaceCollection(
              'My Created Workspaces',
              workspaces.filter((wm) => wm.role === 'OWNER' || wm.workspace.createdBy === wm.userId),
              <Kanban className="w-4 h-4 text-amber-400" />
            )}
            {renderWorkspaceCollection(
              'Invited & Joined Workspaces',
              workspaces.filter((wm) => wm.role !== 'OWNER' && wm.workspace.createdBy !== wm.userId),
              <Users className="w-4 h-4 text-indigo-400" />
            )}
          </div>
        ) : (
          <div className="bg-slate-900/30 border border-slate-800/80 rounded-2xl p-12 text-center max-w-lg mx-auto">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center mx-auto mb-4 text-indigo-400">
              <Kanban className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-200">No Workspaces Found</h3>
            <p className="text-xs text-slate-400 mt-1 mb-6">
              Create your first workspace to start organizing boards, lists, and real-time task cards.
            </p>
            {authenticated && (
              <button
                onClick={() => setShowModal(true)}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-xs rounded-xl shadow-lg shadow-indigo-600/20 inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Create Workspace Now
              </button>
            )}
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-1">Create Workspace</h3>
            <p className="text-xs text-slate-400 mb-5">Set up a new space for team boards and collaboration</p>
            <form onSubmit={handleCreateWorkspace} className="space-y-4">
              {errorMessage && (
                <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-xs text-rose-400">
                  {errorMessage}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Workspace Name</label>
                <input
                  type="text"
                  placeholder="e.g. Engineering Team"
                  value={newWsName}
                  onChange={(e) => {
                    setNewWsName(e.target.value);
                    setNewWsSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'));
                  }}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2 text-sm text-slate-100 focus:outline-none focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Workspace Slug (URL friendly)</label>
                <input
                  type="text"
                  placeholder="engineering-team"
                  value={newWsSlug}
                  onChange={(e) => setNewWsSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'))}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2 text-sm font-mono text-slate-100 focus:outline-none focus:border-indigo-500"
                  required
                />
                <p className="text-[10px] text-slate-500 mt-1">Spaces are automatically converted to hyphens (-)</p>
              </div>
              <div className="flex items-center justify-end gap-3 mt-6 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-xs font-medium text-slate-400 hover:text-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-xs rounded-xl shadow-lg shadow-indigo-600/20"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <KycSubmissionModal
        isOpen={showKycModal}
        onClose={() => setShowKycModal(false)}
        onSuccess={fetchUserData}
        existingKyc={currentUser?.organizationKyc}
        kycStatus={currentUser?.kycStatus || currentUser?.organizationKyc?.status}
      />
    </div>
  );
}
